/**
 * Schema Preview Panel - Phase 3 of Knowledge Graph Design
 *
 * Provides comprehensive schema preview including:
 * - Mermaid ER diagram visualization
 * - Question coverage analysis
 * - GDS algorithm recommendations
 * - Schema expansion suggestions
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Eye,
  LayoutDashboard,
  BarChart3,
  Sparkles,
  Lightbulb,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  ChevronRight,
  ExternalLink,
  Code,
  Copy,
  Check,
  Save,
  Loader2,
} from 'lucide-react';
import {
  generateSchemaPreview,
  getCoverageAnalysis,
  getGDSRecommendations,
  getAnswerableQuestions,
  generateKGFirstSchema,
  getKGFirstSchema,
  updateKGFirstSchema,
  type KGFirstSchemaResponse,
  type KGFirstConcept,
  type KGFirstRelationship,
  type KGFirstSchemaEditPayload,
} from '../../lib/api/knowledgeGraphDesignApi';
import type {
  SchemaPreview,
  CoverageAnalysis,
  QuestionCoverage,
  GDSRecommendation,
} from '../../types/knowledgeGraphDesign';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import Card from '../ui/Card';
import Spinner from '../ui/Spinner';
import MermaidDiagram from './MermaidDiagram';
import ReactFlowSchemaGraph from './ReactFlowSchemaGraph';
import GDSRecommendations from './GDSRecommendations';
import { NewNodeData } from './AddNodeModal';
import { EditNodeData } from './EditNodeModal';
import { EditRelationshipData } from './EditRelationshipModal';

interface SchemaPreviewPanelProps {
  caseId: string;
  systemIds: string[];
  systemNames: string[];
  onSchemaLoaded?: (schema: SchemaPreview) => void;
  /**
   * Use KG-First schema generation (concept-first approach).
   * When true, uses generateKGFirstSchema instead of generateSchemaPreview.
   * This maps tables to KG Area taxonomy concepts rather than 1:1 table-to-node conversion.
   */
  useKGFirst?: boolean;
}

type TabId = 'diagram' | 'coverage' | 'gds' | 'expansion' | 'cypher';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'diagram', label: 'Schema Diagram', icon: LayoutDashboard },
  { id: 'coverage', label: 'Question Coverage', icon: BarChart3 },
  { id: 'gds', label: 'GDS Recommendations', icon: Sparkles },
  { id: 'expansion', label: 'Expansion Ideas', icon: Lightbulb },
  { id: 'cypher', label: 'Cypher DDL', icon: Code },
];

export const SchemaPreviewPanel: React.FC<SchemaPreviewPanelProps> = ({
  caseId,
  systemIds,
  systemNames,
  onSchemaLoaded,
  useKGFirst = false,
}) => {
  // State for currently selected system in the tab view
  const [activeSystemIndex, setActiveSystemIndex] = useState(0);
  const activeSystemId = systemIds[activeSystemIndex];
  const activeSystemName = systemNames[activeSystemIndex];

  const [activeTab, setActiveTab] = useState<TabId>('diagram');
  const [diagramView, setDiagramView] = useState<'reactflow' | 'mermaid'>('reactflow');
  const [copied, setCopied] = useState(false);

  // Local relationship type for schema editor
  interface LocalRelationship {
    relationship_id: string;
    relationship_type: string;
    source_concept: string;
    target_concept: string;
    cardinality: string;
    description: string;
  }

  // State for locally added relationships (before save to backend)
  const [localRelationships, setLocalRelationships] = useState<LocalRelationship[]>([]);

  // Local node type for schema editor (matches PreviewTable)
  interface LocalNode {
    table_id: string;
    table_name: string;
    node_label: string;
    module_name: string;
    columns: { column_name: string; data_type: string; is_primary_key: boolean; is_foreign_key: boolean }[];
    description?: string;
    is_central: boolean;
    connection_count: number;
  }

  // State for locally added nodes (before save to backend)
  const [localNodes, setLocalNodes] = useState<LocalNode[]>([]);

  // State for deleted elements (before save to backend)
  const [deletedNodeIds, setDeletedNodeIds] = useState<Set<string>>(new Set());
  const [deletedEdgeIds, setDeletedEdgeIds] = useState<Set<string>>(new Set());

  // State for edited nodes (tracked separately from local nodes)
  interface EditedNode {
    nodeId: string;
    category?: string;
    description?: string;
    properties?: { name: string; type: string; isPrimaryKey: boolean }[];
  }
  const [editedNodes, setEditedNodes] = useState<Map<string, EditedNode>>(new Map());

  // State for edited relationships
  interface EditedRelationship {
    edgeId: string;
    relationshipType?: string;
    cardinality?: string;
    description?: string;
  }
  const [editedRelationships, setEditedRelationships] = useState<Map<string, EditedRelationship>>(new Map());

  // State for save operation
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Check if there are any unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return (
      localNodes.length > 0 ||
      localRelationships.length > 0 ||
      deletedNodeIds.size > 0 ||
      deletedEdgeIds.size > 0 ||
      editedNodes.size > 0 ||
      editedRelationships.size > 0
    );
  }, [localNodes, localRelationships, deletedNodeIds, deletedEdgeIds, editedNodes, editedRelationships]);

  // Fetch KG-First schema (concept-first approach)
  // First try to get saved schema (GET), then generate if not found (POST)
  const {
    data: kgFirstSchema,
    isLoading: loadingKGFirst,
    error: kgFirstError,
    refetch: refetchKGFirst,
  } = useQuery({
    queryKey: ['kg-first-schema', caseId],
    queryFn: async () => {
      try {
        // First try to get existing saved schema
        const saved = await getKGFirstSchema(caseId);
        console.log('✅ Found saved KG-first schema for case:', caseId);
        return saved;
      } catch (error: any) {
        // If 404 (not found), generate new schema
        if (error.response?.status === 404) {
          console.log('📭 No saved schema found, generating new one for case:', caseId);
          return await generateKGFirstSchema(caseId);
        }
        throw error;
      }
    },
    enabled: useKGFirst,
    staleTime: 5 * 60 * 1000, // 5 minutes - schema generation is expensive
  });

  // Fetch legacy schema preview for active system
  const {
    data: legacyPreview,
    isLoading: loadingLegacy,
    error: legacyError,
    refetch: refetchLegacy,
  } = useQuery({
    queryKey: ['schema-preview', caseId, activeSystemId],
    queryFn: () => generateSchemaPreview(caseId, activeSystemId, true, true),
    enabled: !useKGFirst && !!activeSystemId,
  });

  // Unified loading/error/data based on mode
  const loadingPreview = useKGFirst ? loadingKGFirst : loadingLegacy;
  const previewError = useKGFirst ? kgFirstError : legacyError;
  const refetchPreview = useKGFirst ? refetchKGFirst : refetchLegacy;

  // Convert KG-First schema to preview-compatible format
  const preview: SchemaPreview | undefined = useMemo(() => {
    if (useKGFirst && kgFirstSchema) {
      console.log('🔄 Converting KG-First schema to preview format:', {
        concepts: kgFirstSchema.concepts?.length,
        relationships: kgFirstSchema.relationships?.length,
        firstConceptProperties: kgFirstSchema.concepts?.[0]?.properties
      });
      // Convert KG-First response to SchemaPreview format for compatibility
      const questionsAddressed = kgFirstSchema.questions_addressed || [];
      const converted: SchemaPreview = {
        case_id: kgFirstSchema.case_id,
        system_id: 'kg_first', // Not system-specific in KG-First mode
        tables: (kgFirstSchema.concepts || []).map((concept: KGFirstConcept, index: number) => ({
          table_id: concept.concept_id,
          table_name: concept.concept_name,
          node_label: concept.concept_name,
          module_name: concept.kg_area_name,
          columns: (concept.properties || [])
            .filter((prop: any) => prop && (prop.name || prop.column_name))
            .map((prop: any) => {
              const propName = prop.name || prop.column_name;
              return {
                column_name: propName,
                data_type: prop.type || prop.data_type || 'String',
                is_primary_key: prop.is_primary_key || (propName || '').toLowerCase().includes('id'),
                is_foreign_key: prop.is_foreign_key || false,
              };
            }),
          is_central: concept.is_master_data || false,
          connection_count: concept.addresses_questions?.length || 0,
          // Extended fields for KG-First (will be available via type assertion)
          kg_area_id: concept.kg_area_id,
          kg_area_name: concept.kg_area_name,
          source_tables: concept.source_tables,
          is_master_data: concept.is_master_data,
          is_transactional: concept.is_transactional,
          addresses_questions: concept.addresses_questions,
          description: concept.description,
        })),
        relationships: (kgFirstSchema.relationships || []).map((rel: KGFirstRelationship) => {
          // Backend returns source_table/target_table for KG-First, not source_concept/target_concept
          const relAny = rel as any;
          const sourceTable = relAny.source_table || rel.source_concept || '';
          const targetTable = relAny.target_table || rel.target_concept || '';
          return {
            relationship_id: rel.relationship_id,
            relationship_type: rel.relationship_type,
            source_table: sourceTable,
            target_table: targetTable,
            source_node_label: relAny.source_node_label || sourceTable,
            target_node_label: relAny.target_node_label || targetTable,
            cardinality: rel.cardinality as any,
            source_column: rel.source_column || '',
            target_column: rel.target_column || '',
            // Extended fields for KG-First
            business_process: rel.business_process,
            derived_from_fk: rel.derived_from_fk,
            description: rel.description,
          };
        }),
        mermaid_diagram: kgFirstSchema.mermaid_diagram,
        cypher_ddl: kgFirstSchema.cypher_ddl,
        coverage_analysis: {
          overall_score: kgFirstSchema.question_coverage_score,
          questions_fully_covered: questionsAddressed.filter((q: any) => q.covered).length,
          questions_partially_covered: 0,
          questions_not_covered: questionsAddressed.filter((q: any) => !q.covered).length,
          total_questions: questionsAddressed.length,
          question_coverage: questionsAddressed.map((q: any) => ({
            question_id: q.question_id,
            question_text: q.text,
            coverage_level: q.covered ? 'full' : 'none',
            coverage_score: q.covered ? 1.0 : 0.0,
            category: '',
            required_entities: [],
            available_entities: [],
            missing_entities: [],
            required_relationships: [],
            available_relationships: [],
            missing_relationships: [],
            analysis_notes: '',
          })),
          recommendations: [],
        },
        // Convert GDS recommendations from KG-First format to standard format
        gds_recommendations: (kgFirstSchema.gds_recommendations || []).map((rec: any) => ({
          algorithm_name: rec.algorithm_name,
          algorithm_category: rec.algorithm_category,
          use_case: rec.use_case,
          applicable_nodes: rec.applicable_nodes || [],
          applicable_relationships: rec.applicable_relationships || [],
          business_value: rec.business_value,
          example_query: rec.example_query,
          priority: rec.priority,
          complexity: rec.complexity || 'medium',
        })),
        // Convert expansion ideas from KG-First format to standard suggestions format
        expansion_suggestions: (kgFirstSchema.expansion_ideas || []).map((idea: any, index: number) => ({
          suggestion_id: `exp_${index}`,
          suggestion_type: 'add_table' as const,
          target_element: idea.title,
          reason: idea.description,
          impact_score: idea.priority === 'high' ? 0.9 : idea.priority === 'medium' ? 0.6 : 0.3,
          questions_addressed: [],
          implementation_notes: idea.business_benefit,
          // Extended fields for expansion ideas
          new_entities: idea.new_entities || [],
          new_relationships: idea.new_relationships || [],
          kg_area: idea.kg_area,
          complexity: idea.complexity,
          priority: idea.priority,
        })),
        generated_at: kgFirstSchema.generated_at,
        processing_time_seconds: 0,
      };
      // Add KG-First specific metadata (accessible via type assertion)
      (converted as any).kg_areas_used = kgFirstSchema.kg_areas_used;
      (converted as any).schema_type = 'kg_first';
      return converted;
    }
    return legacyPreview;
  }, [useKGFirst, kgFirstSchema, legacyPreview]);

  // Merge local changes (added nodes, relationships, deleted items) into preview for display
  const previewWithLocalChanges: SchemaPreview | undefined = useMemo(() => {
    if (!preview) return undefined;

    // Filter out deleted nodes
    const filteredTables = (preview.tables || []).filter(
      (table) => !deletedNodeIds.has(table.table_id)
    );

    // Add local nodes
    const allTables = [...filteredTables, ...localNodes];

    // Filter out deleted edges and add local relationships
    const existingRels = (preview.relationships || []).filter(
      (rel) => !deletedEdgeIds.has(rel.relationship_id)
    );

    // Also filter out relationships connected to deleted nodes
    const validExistingRels = existingRels.filter(
      (rel) => !deletedNodeIds.has(rel.source_table) && !deletedNodeIds.has(rel.target_table)
    );

    // Convert local relationships to preview format
    const additionalRels = localRelationships.map((rel) => ({
      relationship_id: rel.relationship_id,
      relationship_type: rel.relationship_type,
      source_table: rel.source_concept,
      target_table: rel.target_concept,
      source_node_label: rel.source_concept,
      target_node_label: rel.target_concept,
      cardinality: rel.cardinality as any,
      source_column: '',
      target_column: '',
      description: rel.description,
      isLocal: true, // Mark as locally added
    }));

    return {
      ...preview,
      tables: allTables,
      relationships: [...validExistingRels, ...additionalRels],
    };
  }, [preview, localRelationships, localNodes, deletedNodeIds, deletedEdgeIds]);

  // Handler for adding new relationships from the diagram
  const handleRelationshipAdd = (relationship: {
    sourceId: string;
    targetId: string;
    relationshipType: string;
    cardinality: string;
    description: string;
  }) => {
    const newRelationship: LocalRelationship = {
      relationship_id: `rel_local_${Date.now()}`,
      relationship_type: relationship.relationshipType,
      source_concept: relationship.sourceId,
      target_concept: relationship.targetId,
      cardinality: relationship.cardinality,
      description: relationship.description,
    };

    setLocalRelationships((prev) => [...prev, newRelationship]);
    console.log('✅ Added local relationship:', newRelationship);
  };

  // Handler for deleting nodes from the diagram
  const handleNodeDelete = (nodeId: string) => {
    // Check if it's a locally added node
    const isLocalNode = localNodes.some((node) => node.table_id === nodeId);

    if (isLocalNode) {
      // Remove from local nodes directly
      setLocalNodes((prev) => prev.filter((node) => node.table_id !== nodeId));
    } else {
      // Mark for deletion from server
      setDeletedNodeIds((prev) => new Set([...prev, nodeId]));
    }

    // Also remove any local relationships connected to this node
    setLocalRelationships((prev) =>
      prev.filter((rel) => rel.source_concept !== nodeId && rel.target_concept !== nodeId)
    );
    console.log('🗑️ Deleted node:', nodeId, isLocalNode ? '(local)' : '(from server)');
  };

  // Handler for deleting edges from the diagram
  const handleEdgeDelete = (edgeId: string) => {
    // Check if it's a local relationship
    const isLocalRel = localRelationships.some((rel) => rel.relationship_id === edgeId);
    if (isLocalRel) {
      // Remove from local relationships
      setLocalRelationships((prev) => prev.filter((rel) => rel.relationship_id !== edgeId));
    } else {
      // Mark for deletion from server
      setDeletedEdgeIds((prev) => new Set([...prev, edgeId]));
    }
    console.log('🗑️ Deleted edge:', edgeId, isLocalRel ? '(local)' : '(from server)');
  };

  // Handler for adding new nodes from the diagram
  const handleNodeAdd = (nodeData: NewNodeData) => {
    const newNode: LocalNode = {
      table_id: nodeData.nodeLabel,
      table_name: nodeData.nodeLabel,
      node_label: nodeData.nodeLabel,
      module_name: nodeData.category,
      columns: nodeData.properties.map((p) => ({
        column_name: p.name,
        data_type: p.type,
        is_primary_key: p.isPrimaryKey,
        is_foreign_key: false,
      })),
      description: nodeData.description,
      is_central: false,
      connection_count: 0, // New nodes start with no connections
    };

    setLocalNodes((prev) => [...prev, newNode]);
    console.log('➕ Added local node:', newNode);
  };

  // Handler for editing nodes
  const handleNodeEdit = (editData: EditNodeData) => {
    // Check if it's a locally added node
    const isLocalNode = localNodes.some((node) => node.table_id === editData.nodeId);

    if (isLocalNode) {
      // Update local nodes directly
      setLocalNodes((prev) =>
        prev.map((node) => {
          if (node.table_id === editData.nodeId) {
            return {
              ...node,
              module_name: editData.category,
              description: editData.description,
              columns: editData.properties.map((p) => ({
                column_name: p.name,
                data_type: p.type,
                is_primary_key: p.isPrimaryKey,
                is_foreign_key: false,
              })),
            };
          }
          return node;
        })
      );
    } else {
      // Track edit for server-side node
      setEditedNodes((prev) => {
        const updated = new Map(prev);
        updated.set(editData.nodeId, {
          nodeId: editData.nodeId,
          category: editData.category,
          description: editData.description,
          properties: editData.properties,
        });
        return updated;
      });
    }
    console.log('✏️ Edited node:', editData);
  };

  // Handler for editing relationships
  const handleEdgeEdit = (editData: EditRelationshipData) => {
    // Check if it's a locally added relationship
    const isLocalRelationship = localRelationships.some((rel) => rel.relationship_id === editData.edgeId);

    if (isLocalRelationship) {
      // Update local relationships directly
      setLocalRelationships((prev) =>
        prev.map((rel) => {
          if (rel.relationship_id === editData.edgeId) {
            return {
              ...rel,
              relationship_type: editData.relationshipType,
              cardinality: editData.cardinality,
              description: editData.description,
            };
          }
          return rel;
        })
      );
    } else {
      // Track edit for server-side relationship
      setEditedRelationships((prev) => {
        const updated = new Map(prev);
        updated.set(editData.edgeId, {
          edgeId: editData.edgeId,
          relationshipType: editData.relationshipType,
          cardinality: editData.cardinality,
          description: editData.description,
        });
        return updated;
      });
    }
    console.log('✏️ Edited relationship:', editData);
  };

  // Handler for saving schema changes to Neo4j
  const handleSaveSchema = async () => {
    if (!useKGFirst || !hasUnsavedChanges) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const payload: KGFirstSchemaEditPayload = {};

      // Add new nodes
      if (localNodes.length > 0) {
        payload.added_nodes = localNodes.map((node) => ({
          node_label: node.node_label,
          category: node.module_name,
          description: node.description || `${node.node_label} entity`,
          properties: node.columns.map((col) => ({
            name: col.column_name,
            type: col.data_type,
            is_primary_key: col.is_primary_key,
          })),
        }));
      }

      // Add deleted node IDs
      if (deletedNodeIds.size > 0) {
        payload.deleted_node_ids = Array.from(deletedNodeIds);
      }

      // Add edited nodes
      if (editedNodes.size > 0) {
        payload.updated_nodes = Array.from(editedNodes.values()).map((node) => ({
          node_id: node.nodeId,
          category: node.category,
          description: node.description,
          properties: node.properties?.map((p) => ({
            name: p.name,
            type: p.type,
            is_primary_key: p.isPrimaryKey,
          })),
        }));
      }

      // Add new relationships
      if (localRelationships.length > 0) {
        payload.added_relationships = localRelationships.map((rel) => ({
          relationship_type: rel.relationship_type,
          source_node: rel.source_concept,
          target_node: rel.target_concept,
          cardinality: rel.cardinality,
          description: rel.description,
        }));
      }

      // Add deleted relationship IDs
      if (deletedEdgeIds.size > 0) {
        payload.deleted_relationship_ids = Array.from(deletedEdgeIds);
      }

      // Add edited relationships
      if (editedRelationships.size > 0) {
        payload.updated_relationships = Array.from(editedRelationships.values()).map((rel) => ({
          relationship_id: rel.edgeId,
          relationship_type: rel.relationshipType,
          cardinality: rel.cardinality,
          description: rel.description,
        }));
      }

      console.log('💾 Saving schema changes:', payload);
      const result = await updateKGFirstSchema(caseId, payload);
      console.log('✅ Schema saved successfully:', result);

      // Clear local state after successful save
      setLocalNodes([]);
      setLocalRelationships([]);
      setDeletedNodeIds(new Set());
      setDeletedEdgeIds(new Set());
      setEditedNodes(new Map());
      setEditedRelationships(new Map());

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      // Refetch schema to get updated data
      refetchKGFirst();
    } catch (error: any) {
      console.error('❌ Failed to save schema:', error);
      // Provide helpful error messages for common issues
      if (error.response?.status === 405) {
        setSaveError('Save endpoint not available yet. Schema edits are stored locally until the backend endpoint is implemented.');
      } else if (error.response?.status === 404) {
        setSaveError('Schema not found. Please regenerate the schema first.');
      } else if (error.response?.status === 500) {
        setSaveError('Server error. Please try again later.');
      } else {
        setSaveError(error.message || 'Failed to save schema changes');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Notify parent when schema preview is loaded
  useEffect(() => {
    if (preview && onSchemaLoaded) {
      onSchemaLoaded(preview);
    }
  }, [preview, onSchemaLoaded]);

  // Coverage score color
  const getCoverageColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    if (score >= 0.4) return 'text-orange-600';
    return 'text-red-600';
  };

  // Coverage level badge
  const getCoverageBadge = (level: string) => {
    switch (level) {
      case 'full':
        return (
          <Badge className="bg-green-100 text-green-700">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Full Coverage
          </Badge>
        );
      case 'partial':
        return (
          <Badge className="bg-yellow-100 text-yellow-700">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Partial
          </Badge>
        );
      default:
        return (
          <Badge className="bg-red-100 text-red-700">
            <AlertCircle className="w-3 h-3 mr-1" />
            Not Covered
          </Badge>
        );
    }
  };

  // Render coverage analysis
  const renderCoverageAnalysis = () => {
    if (!preview?.coverage_analysis) {
      return (
        <div className="text-center py-12 text-neutral-500">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
          <p>No coverage analysis available</p>
        </div>
      );
    }

    const { coverage_analysis } = preview;

    return (
      <div className="space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <div className={`text-3xl font-bold ${getCoverageColor(coverage_analysis.overall_score)}`}>
              {Math.round(coverage_analysis.overall_score * 100)}%
            </div>
            <div className="text-sm text-neutral-500 mt-1">Overall Coverage</div>
          </Card>
          <Card className="p-4 text-center bg-green-50">
            <div className="text-3xl font-bold text-green-600">
              {coverage_analysis.questions_fully_covered}
            </div>
            <div className="text-sm text-green-700 mt-1">Fully Covered</div>
          </Card>
          <Card className="p-4 text-center bg-yellow-50">
            <div className="text-3xl font-bold text-yellow-600">
              {coverage_analysis.questions_partially_covered}
            </div>
            <div className="text-sm text-yellow-700 mt-1">Partially Covered</div>
          </Card>
          <Card className="p-4 text-center bg-red-50">
            <div className="text-3xl font-bold text-red-600">
              {coverage_analysis.questions_not_covered}
            </div>
            <div className="text-sm text-red-700 mt-1">Not Covered</div>
          </Card>
        </div>

        {/* Recommendations */}
        {coverage_analysis.recommendations && coverage_analysis.recommendations.length > 0 && (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Recommendations
            </h4>
            <ul className="space-y-1">
              {coverage_analysis.recommendations.map((rec, idx) => (
                <li key={idx} className="text-sm text-blue-700 flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {rec}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Question coverage details */}
        <div>
          <h4 className="font-medium mb-3">Question Coverage Details</h4>
          <div className="space-y-3">
            {coverage_analysis.question_coverage?.map((q) => (
              <Card key={q.question_id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getCoverageBadge(q.coverage_level)}
                      <span className={`text-sm font-medium ${getCoverageColor(q.coverage_score)}`}>
                        {Math.round(q.coverage_score * 100)}%
                      </span>
                    </div>
                    <p className="text-sm font-medium">{q.question_text}</p>
                    <p className="text-xs text-neutral-500 mt-1">Category: {q.category}</p>
                  </div>
                </div>

                {/* Missing elements */}
                {(q.missing_entities.length > 0 || q.missing_relationships.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-neutral-100">
                    <p className="text-xs text-neutral-500 mb-2">Missing data:</p>
                    <div className="flex flex-wrap gap-1">
                      {q.missing_entities.map((entity) => (
                        <Badge key={entity} variant="outline" className="text-xs text-red-600">
                          Entity: {entity}
                        </Badge>
                      ))}
                      {q.missing_relationships.map((rel) => (
                        <Badge key={rel} variant="outline" className="text-xs text-orange-600">
                          Rel: {rel}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analysis notes */}
                {q.analysis_notes && (
                  <p className="text-xs text-neutral-500 mt-2 italic">{q.analysis_notes}</p>
                )}
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render expansion suggestions
  const renderExpansionSuggestions = () => {
    if (!preview?.expansion_suggestions || preview.expansion_suggestions.length === 0) {
      return (
        <div className="text-center py-12 text-neutral-500">
          <Lightbulb className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
          <p>No expansion suggestions available</p>
          <p className="text-sm mt-1">Your schema appears to be comprehensive</p>
        </div>
      );
    }

    // Priority colors
    const getPriorityColor = (priority: string) => {
      switch (priority) {
        case 'high':
          return 'bg-red-100 text-red-700 border-red-200';
        case 'medium':
          return 'bg-yellow-100 text-yellow-700 border-yellow-200';
        case 'low':
          return 'bg-green-100 text-green-700 border-green-200';
        default:
          return 'bg-neutral-100 text-neutral-700';
      }
    };

    // Complexity colors
    const getComplexityColor = (complexity: string) => {
      switch (complexity) {
        case 'low':
          return 'text-green-600';
        case 'medium':
          return 'text-yellow-600';
        case 'high':
          return 'text-red-600';
        default:
          return 'text-neutral-600';
      }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="w-5 h-5 text-yellow-500" />
          <h4 className="font-medium">Schema Expansion Ideas</h4>
          <Badge variant="outline">{preview.expansion_suggestions.length} ideas</Badge>
        </div>

        {preview.expansion_suggestions.map((suggestion: any) => (
          <Card key={suggestion.suggestion_id} className="p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Header with priority badge */}
                <div className="flex items-center gap-2 mb-2">
                  {suggestion.priority && (
                    <Badge className={getPriorityColor(suggestion.priority)} variant="outline">
                      {suggestion.priority} priority
                    </Badge>
                  )}
                  {suggestion.kg_area && (
                    <Badge variant="secondary" className="text-xs">
                      {suggestion.kg_area}
                    </Badge>
                  )}
                </div>

                {/* Title */}
                <h5 className="font-semibold text-base mb-2">{suggestion.target_element}</h5>

                {/* Description */}
                <p className="text-sm text-neutral-600 mb-3">{suggestion.reason}</p>

                {/* New entities and relationships for KG-First */}
                {(suggestion.new_entities?.length > 0 || suggestion.new_relationships?.length > 0) && (
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {suggestion.new_entities?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          New Entities
                        </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {suggestion.new_entities.map((entity: string) => (
                            <Badge key={entity} variant="outline" className="text-xs bg-blue-50">
                              {entity}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {suggestion.new_relationships?.length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          New Relationships
                        </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {suggestion.new_relationships.map((rel: string) => (
                            <Badge key={rel} variant="secondary" className="text-xs">
                              {rel}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Complexity and impact */}
                <div className="flex items-center gap-4 mt-3">
                  {suggestion.complexity && (
                    <span className="text-xs text-neutral-500">
                      Complexity:{' '}
                      <span className={`font-medium ${getComplexityColor(suggestion.complexity)}`}>
                        {suggestion.complexity.charAt(0).toUpperCase() + suggestion.complexity.slice(1)}
                      </span>
                    </span>
                  )}
                  <span className="text-xs text-neutral-500">
                    Impact:{' '}
                    <span className={getCoverageColor(suggestion.impact_score)}>
                      {Math.round(suggestion.impact_score * 100)}%
                    </span>
                  </span>
                </div>

                {/* Business benefit (implementation notes) */}
                {suggestion.implementation_notes && (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <span className="text-xs font-medium text-green-800 block mb-1">
                      Business Benefit
                    </span>
                    <p className="text-sm text-green-700">{suggestion.implementation_notes}</p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  };

  // Loading state
  if (loadingPreview) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="w-8 h-8" />
        <span className="ml-3 text-neutral-600">Generating schema preview...</span>
      </div>
    );
  }

  // Error state
  if (previewError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-red-600">
        <AlertCircle className="w-8 h-8 mb-2" />
        <span>Failed to generate preview</span>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => refetchPreview()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-neutral-200">
        {/* System tabs (only show if multiple systems) */}
        {systemIds.length > 1 && (
          <div className="flex items-center gap-1 px-4 pt-3 pb-0 border-b border-neutral-100 bg-neutral-50">
            {systemNames.map((name, index) => (
              <button
                key={systemIds[index]}
                onClick={() => setActiveSystemIndex(index)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeSystemIndex === index
                    ? 'bg-white text-primary-700 border border-b-0 border-neutral-200'
                    : 'text-neutral-600 hover:text-neutral-800 hover:bg-neutral-100'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-primary-600" />
            <div>
              <h3 className="font-semibold text-lg">
                {useKGFirst ? 'Knowledge Graph Schema' : 'Schema Preview'}
              </h3>
              {useKGFirst ? (
                <p className="text-sm text-neutral-500">
                  Concept-first design using KG Area taxonomy
                </p>
              ) : (
                activeSystemName && <p className="text-sm text-neutral-500">{activeSystemName}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {preview && (
              <>
                <Badge variant="outline">
                  {preview.tables?.length || 0} {useKGFirst ? 'concepts' : 'tables'}
                </Badge>
                <Badge variant="outline">{preview.relationships?.length || 0} relationships</Badge>
                {useKGFirst && (preview as any).kg_areas_used && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    {(preview as any).kg_areas_used.length} KG Areas
                  </Badge>
                )}
              </>
            )}
            {useKGFirst && hasUnsavedChanges && (
              <>
                <Badge className="bg-amber-100 text-amber-700 border border-amber-300">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Unsaved Changes
                </Badge>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveSchema}
                  disabled={isSaving}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Saving...
                    </>
                  ) : saveSuccess ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-1" />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            )}
            {saveError && (
              <div className="text-sm text-red-600 bg-red-50 px-3 py-1 rounded-lg border border-red-200">
                {saveError}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => refetchPreview()}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-2 border-b border-neutral-100 bg-neutral-50">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'diagram' && preview && (
          <div className="flex flex-col">
            {/* View toggle */}
            <div className="flex items-center justify-end gap-2 mb-3">
              <span className="text-sm text-neutral-500">View:</span>
              <div className="inline-flex rounded-lg border border-neutral-200 p-1 bg-white">
                <button
                  onClick={() => setDiagramView('reactflow')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    diagramView === 'reactflow'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-neutral-600 hover:text-neutral-800'
                  }`}
                >
                  Interactive
                </button>
                <button
                  onClick={() => setDiagramView('mermaid')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    diagramView === 'mermaid'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-neutral-600 hover:text-neutral-800'
                  }`}
                >
                  {useKGFirst ? 'Mermaid' : 'ER Diagram'}
                </button>
              </div>
            </div>

            {/* Diagram content - React Flow requires explicit pixel dimensions */}
            <div style={{ width: '100%', height: '700px' }}>
              {diagramView === 'reactflow' ? (
                <ReactFlowSchemaGraph
                  schemaPreview={previewWithLocalChanges}
                  height={700}
                  className="w-full"
                  editable={true}
                  onRelationshipAdd={handleRelationshipAdd}
                  onNodeAdd={handleNodeAdd}
                  onNodeEdit={handleNodeEdit}
                  onNodeDelete={handleNodeDelete}
                  onEdgeDelete={handleEdgeDelete}
                  onEdgeEdit={handleEdgeEdit}
                  onNodeClick={(nodeId, node) => {
                    console.log('Node clicked:', nodeId, node);
                  }}
                  onEdgeClick={(edgeId, edge) => {
                    console.log('Edge clicked:', edgeId, edge);
                  }}
                />
              ) : preview?.mermaid_diagram ? (
                <MermaidDiagram
                  diagram={preview.mermaid_diagram}
                  title={useKGFirst ? "Knowledge Graph Schema" : "Schema ER Diagram"}
                  className="h-full"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-neutral-500">
                  <p>No {useKGFirst ? 'Knowledge Graph' : 'ER'} diagram available</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'coverage' && renderCoverageAnalysis()}

        {activeTab === 'gds' && (
          <GDSRecommendations recommendations={preview?.gds_recommendations || []} />
        )}

        {activeTab === 'expansion' && renderExpansionSuggestions()}

        {activeTab === 'cypher' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-primary-600" />
                <h4 className="font-medium">Cypher DDL Script</h4>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (preview?.cypher_ddl) {
                    navigator.clipboard.writeText(preview.cypher_ddl);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }
                }}
                disabled={!preview?.cypher_ddl}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy DDL
                  </>
                )}
              </Button>
            </div>

            {preview?.cypher_ddl ? (
              <div className="relative">
                <pre className="bg-neutral-900 text-neutral-100 p-4 rounded-lg overflow-x-auto text-sm font-mono leading-relaxed max-h-[600px] overflow-y-auto">
                  <code>{preview.cypher_ddl}</code>
                </pre>
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-500">
                <Code className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                <p>No Cypher DDL available</p>
                <p className="text-sm mt-1">Schema preview needs to generate DDL first</p>
              </div>
            )}

            <Card className="p-4 bg-blue-50 border-blue-200">
              <h5 className="font-medium text-blue-800 mb-2">Usage Instructions</h5>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>1. Execute constraint statements first (they create implicit indexes)</li>
                <li>2. Execute additional index statements</li>
                <li>3. Use node/relationship examples as templates for data ingestion</li>
                <li>4. For bulk loading, consider using LOAD CSV or APOC procedures</li>
              </ul>
            </Card>
          </div>
        )}
      </div>

      {/* Footer with stats */}
      {preview && (
        <div className="p-3 border-t border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between text-sm text-neutral-500">
            <span>
              Generated at: {preview.generated_at ? new Date(preview.generated_at).toLocaleString() : 'N/A'}
            </span>
            <span>
              Processing time: {(preview.processing_time_seconds ?? 0).toFixed(2)}s
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemaPreviewPanel;
